import React from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import EntityLink from "./EntityLink";
import { formatDate, formatDateTime } from "../utils/date";

/**
 * Generic record-detail dialog — Phase UX-4 refresh.
 *
 * Backwards compatible: still accepts { row, onClose, title, actions, children }.
 * NEW props (all optional):
 *   icon     – lucide-react icon component to render left of the title.
 *   subtitle – small line under the title (breadcrumb-style context).
 *   size     – "md" (max-w-xl) | "lg" (max-w-2xl, default) | "xl" (max-w-3xl).
 *
 * Field auto-grouping (NEW):
 *   Fields are bucketed into sections (Person / Timeline / Amounts / Approval / Notes / System)
 *   based on key patterns so every modal gets consistent visual hierarchy without callers
 *   having to hand-configure sections.
 */

const NAME_SIBLINGS = ["_display", "_name"];

const UUID_FIELDS = new Set([
    "employee_id", "assigned_employee_id", "signed_by_employee",
    "module_id",
    "created_by", "assigned_by", "issued_by", "closed_by",
    "dept_decision_by", "hr_decision_by",
    "acknowledged_by", "signed_by", "updated_by",
    "released_by", "received_by", "approved_by",
    "prepared_by", "dept_confirmed_by", "finance_reviewed_by",
    "actor_user_id",
    "linked_contract_id", "linked_payroll_line_id", "shift_rule_id",
]);

function IS_SIBLING_SUFFIX(k) {
    if (!/_(name|display|code)$/.test(k)) return false;
    const stem = k.replace(/_(name|display|code)$/, "");
    return UUID_FIELDS.has(stem + "_id") || UUID_FIELDS.has(stem);
}

const DATE_FIELD = /(^|_)date$|^date$/i;
const DATETIME_FIELD = /_at$|^at$/i;

function humanLabel(k) {
    return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function pickHumanValue(row, key) {
    const stem = key.endsWith("_id") ? key.slice(0, -3) : key;
    for (const suf of NAME_SIBLINGS) {
        const sib = stem + suf;
        if (sib in row) {
            const v = row[sib];
            if (v !== null && v !== undefined && v !== "") return v;
        }
    }
    return null;
}

function renderValue(k, v) {
    if (v === null || v === undefined || v === "") {
        return <span style={{ color: "var(--asl-text-3)" }}>—</span>;
    }
    if (typeof v === "object") {
        return <pre className="bg-[color:var(--asl-bg-alt)] p-2 rounded text-[11px] overflow-auto">{JSON.stringify(v, null, 2)}</pre>;
    }
    if (typeof v === "string") {
        if (DATETIME_FIELD.test(k) && /^\d{4}-\d{2}-\d{2}[T ]/.test(v)) return <span className="mono">{formatDateTime(v)}</span>;
        if (DATE_FIELD.test(k) && /^\d{4}-\d{2}-\d{2}$/.test(v)) return <span className="mono">{formatDate(v)}</span>;
    }
    return <span>{String(v)}</span>;
}

// Field → section mapping. Uses regex-first matching (order matters).
const SECTION_RULES = [
    { section: "Person",        keys: /^(employee|assigned|module|home_dept|job_title|worker_category|role)|_id$/i },
    { section: "Timeline",      keys: /(date|_at$|duration|days|period|window|start|end|effective|expiry)/i },
    { section: "Approval",      keys: /(status|approv|dept_decision|hr_decision|acknowledg|signed|closed|reviewed|confirmed)/i },
    { section: "Amounts",       keys: /(amount|cost|salary|rate|net|gross|overtime|uplift|allowance|deduction|fee|paid|funding|outstanding|price|value|balance)/i },
    { section: "Details",       keys: /(type|category|title|description|reason|note|priority|condition|item|size|quantity|attachments?|documents?)/i },
    { section: "System",        keys: /(created|updated|reference|id$|ip$|source|version)/i },
];

function bucketize(rendered) {
    const buckets = { Person: [], Details: [], Timeline: [], Amounts: [], Approval: [], Notes: [], System: [], Other: [] };
    for (const row of rendered) {
        const [k] = row;
        let placed = false;
        for (const rule of SECTION_RULES) {
            if (rule.keys.test(k)) {
                (buckets[rule.section] ||= []).push(row);
                placed = true;
                break;
            }
        }
        if (!placed) buckets.Other.push(row);
    }
    // Return the sections in a stable, human-friendly order.
    return [
        ["Person", buckets.Person],
        ["Details", buckets.Details],
        ["Timeline", buckets.Timeline],
        ["Amounts", buckets.Amounts],
        ["Approval", buckets.Approval],
        ["Notes", buckets.Notes],
        ["Other", buckets.Other],
        ["System", buckets.System],
    ].filter(([, rows]) => rows.length > 0);
}

const SIZE_CLASSES = { md: "max-w-xl", lg: "max-w-2xl", xl: "max-w-3xl" };

export default function DetailDialog({
    row,
    onClose,
    title = "Details",
    subtitle,
    icon: Icon,
    size = "lg",
    testid = "detail-dialog",
    actions = [],
    children = null,
    moduleCodeFallback = null,
    footerCloseLabel = "Close",
}) {
    const navigate = useNavigate();
    const open = !!row;
    const modCode = (row && (row.module_code || row.module?.code)) || moduleCodeFallback;

    const linkFor = (k) => {
        if (!row || !modCode) return null;
        if (k === "employee_id" || k === "assigned_employee_id" || k === "signed_by_employee") {
            const id = row[k];
            return id ? `/m/${String(modCode).toLowerCase()}/staff/${id}` : null;
        }
        if (k === "module_id") return `/m/${String(modCode).toLowerCase()}`;
        return null;
    };

    const rendered = [];
    if (row) {
        for (const [k, v] of Object.entries(row)) {
            if (k.startsWith("_")) continue;
            if (k === "id") continue;
            if (IS_SIBLING_SUFFIX(k)) continue;
            if (UUID_FIELDS.has(k) && pickHumanValue(row, k) !== null) {
                const humanKey = k.replace(/_by$|_id$/, "").replace(/^assigned_employee$/, "employee");
                const label = humanLabel(humanKey === k ? k : humanKey);
                rendered.push([k, label, pickHumanValue(row, k)]);
                continue;
            }
            if (UUID_FIELDS.has(k) && !pickHumanValue(row, k)) {
                if (!v) continue;
                rendered.push([k, humanLabel(k), v]);
                continue;
            }
            rendered.push([k, humanLabel(k), v]);
        }
    }
    const sections = bucketize(rendered);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
            <DialogContent className={`${SIZE_CLASSES[size] || SIZE_CLASSES.lg} max-h-[85vh] overflow-auto`} data-testid={testid}>
                <DialogHeader className="pb-3" style={{ borderBottom: "1px solid var(--asl-divider)" }}>
                    <div className="flex items-start gap-3">
                        {Icon && (
                            <div className="p-2 rounded-lg flex-shrink-0" style={{ background: "var(--asl-bg-alt)", color: "var(--asl-navy-2)" }}>
                                <Icon className="h-5 w-5" />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="text-section-title" data-testid={`${testid}-title`}>{title}</DialogTitle>
                            {subtitle && (
                                <div className="text-body-muted mt-1" data-testid={`${testid}-subtitle`}>{subtitle}</div>
                            )}
                        </div>
                    </div>
                </DialogHeader>
                {children}
                {row && sections.length > 0 && (
                    <div className="space-y-5 pt-2">
                        {sections.map(([sectionName, rows]) => (
                            <div key={sectionName} data-testid={`${testid}-section-${sectionName.toLowerCase()}`}>
                                <div className="text-label mb-2 pb-1" style={{ borderBottom: "1px solid var(--asl-divider)" }}>
                                    {sectionName}
                                </div>
                                <div className="space-y-1.5">
                                    {rows.map(([k, label, value]) => {
                                        const to = linkFor(k);
                                        return (
                                            <div key={k} className="grid grid-cols-3 gap-3 items-baseline">
                                                <div className="text-body-muted text-xs col-span-1">{label}</div>
                                                <div className="col-span-2 text-sm break-words" style={{ color: "var(--asl-text)", fontWeight: 500 }}>
                                                    {to ? (
                                                        <EntityLink to={to} onNav={() => onClose && onClose()} testid={`${testid}-link-${k}`}>
                                                            {renderValue(k, value)}
                                                        </EntityLink>
                                                    ) : renderValue(k, value)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                        {row.id && (
                            <div className="pt-3 mt-2 text-[10px]" style={{ color: "var(--asl-text-3)", borderTop: "1px solid var(--asl-divider)" }}>
                                Reference · <span className="mono">{row.id}</span>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter className="pt-4 flex-row justify-between sm:justify-between" style={{ borderTop: "1px solid var(--asl-divider)" }}>
                    <Button variant="ghost" onClick={onClose} data-testid={`${testid}-close`}>{footerCloseLabel}</Button>
                    <div className="flex items-center gap-2">
                        {actions.filter((a) => a && a.visible !== false).map((a, i) => (
                            <Button
                                key={i}
                                variant={a.variant || "default"}
                                onClick={a.onClick}
                                className={a.className || ""}
                                data-testid={a.testid || `${testid}-action-${i}`}
                            >
                                {a.label}
                            </Button>
                        ))}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
